import { KeyboardEvent, forwardRef } from "react";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Ripple } from "primereact/ripple";
import { ROW_CLASSES } from "./sidebarRowClasses";

/**
 * Campo de búsqueda del sidebar — SYSTEM_DESIGN.md §4.
 *
 * Es solo la presentación: el estado y el filtrado viven en SidebarNav y
 * navSearch.ts. Colapsado no hay ancho para un input, así que se dibuja una
 * fila con la lupa que expande el menú y enfoca el campo.
 */
interface SidebarSearchProps {
  value: string;
  collapsed: boolean;
  /** Cantidad de resultados, o null cuando no hay búsqueda activa. */
  resultCount: number | null;
  onChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onClear: () => void;
  /** Colapsado no hay campo: expandir el menú y enfocarlo. */
  onExpand: () => void;
}

export const SidebarSearch = forwardRef<HTMLInputElement, SidebarSearchProps>(
  ({ value, collapsed, resultCount, onChange, onKeyDown, onClear, onExpand }, ref) => {
    // Los `key` distintos fuerzan el remount al expandir o colapsar: con el
    // mismo <div> reusado, la animación de entrada del campo no vuelve a correr.
    if (collapsed) {
      return (
        <div key="collapsed" className="app-sidebar__search app-sidebar__search--collapsed">
          <button
            type="button"
            onClick={onExpand}
            className={`${ROW_CLASSES} border-none bg-transparent`}
            aria-label="Buscar en el menú"
            title="Buscar en el menú"
          >
            <i className="pi pi-search" />
            <Ripple />
          </button>
        </div>
      );
    }

    return (
      <div key="expanded" className="app-sidebar__search" role="search">
        <div className="app-sidebar__search-field">
          <IconField iconPosition="left" className="w-full">
            <InputIcon className="pi pi-search" />
            <InputText
              ref={ref}
              value={value}
              onChange={(event) => onChange(event.currentTarget.value)}
              onKeyDown={onKeyDown}
              className="w-full"
              placeholder="Buscar"
              aria-label="Buscar en el menú"
              title="Buscar en el menú (Ctrl/⌘ + K)"
              autoComplete="off"
              spellCheck={false}
            />
          </IconField>

          {/* Va FUERA del IconField: ese componente le clona `iconPosition` a
              todos sus hijos, y sobre un <button> del DOM eso es un warning. */}
          {value !== "" && (
            <button
              type="button"
              onClick={onClear}
              className="app-sidebar__search-clear"
              aria-label="Limpiar búsqueda"
              title="Limpiar búsqueda"
            >
              <i className="pi pi-times" />
            </button>
          )}
        </div>

        {/* Lectores de pantalla: sin esto, filtrar la lista no anuncia nada. */}
        <span className="app-sidebar__sr-only" role="status" aria-live="polite">
          {resultCount === null
            ? ""
            : `${resultCount} ${resultCount === 1 ? "resultado" : "resultados"}`}
        </span>
      </div>
    );
  }
);

SidebarSearch.displayName = "SidebarSearch";
